import React from 'react';
import PropTypes from 'prop-types';
import { I18n } from 'react-redux-i18n';
import { ADELANTO_TRANSACTION_STATUS } from '../../../constants';
import { formatAdelantoNexAmount } from '../../../adelanto/adelantoNexAmount';
import './AdelantoStatusBadge.scss';

/*
 * FR-05 — Adelanto NEX version-header status pill (read-only).
 *
 * Header variant (version form):
 *   - NOT_SENT → "Envío NEX Pendiente"
 *   - sent     → "%{count} NEX Enviados" (count from unidades_por_compra until BA-11)
 *
 * Inline variant (monitor tables): plain coloured text per adelanto_status.
 */

const STATUS_TO_TOKEN = {
    [ADELANTO_TRANSACTION_STATUS.NOT_SENT]: 'notSent',
    [ADELANTO_TRANSACTION_STATUS.SENT]: 'sent',
    [ADELANTO_TRANSACTION_STATUS.PARTIAL]: 'partial',
    [ADELANTO_TRANSACTION_STATUS.COMPLETED]: 'completed',
    [ADELANTO_TRANSACTION_STATUS.EXCEEDED]: 'exceeded',
};

const STATUS_TO_INLINE_COLOR = {
    [ADELANTO_TRANSACTION_STATUS.NOT_SENT]: 'gray',
    [ADELANTO_TRANSACTION_STATUS.SENT]: 'inherit',
    [ADELANTO_TRANSACTION_STATUS.PARTIAL]: 'inherit',
    [ADELANTO_TRANSACTION_STATUS.COMPLETED]: 'green',
    [ADELANTO_TRANSACTION_STATUS.EXCEEDED]: '#c75959',
};

const AdelantoStatusBadge = ({ status, className = '', variant = 'header', nexAmount }) => {
    const safeStatus = STATUS_TO_TOKEN[status] ? status : ADELANTO_TRANSACTION_STATUS.NOT_SENT;
    const token = STATUS_TO_TOKEN[safeStatus];
    const isNotSent = safeStatus === ADELANTO_TRANSACTION_STATUS.NOT_SENT;

    const INLINE_LABEL_BY_STATUS = {
        [ADELANTO_TRANSACTION_STATUS.NOT_SENT]: 'No enviado',
        [ADELANTO_TRANSACTION_STATUS.SENT]: 'Enviado',
        [ADELANTO_TRANSACTION_STATUS.PARTIAL]: 'Parcialmente rendido',
        [ADELANTO_TRANSACTION_STATUS.COMPLETED]: 'Rendido',
        [ADELANTO_TRANSACTION_STATUS.EXCEEDED]: I18n.t('contract.adelantoNex.status.exceeded'),
    };

    if (variant === 'inline') {
        return (
            <span
                className={`text adelanto-status adelanto-status--inline adelanto-status-${token} ${className}`}
                data-status={status}
                style={{ color: STATUS_TO_INLINE_COLOR[safeStatus] }}
            >
                {INLINE_LABEL_BY_STATUS[safeStatus]}
            </span>
        );
    }

    const pillLabel = isNotSent
        ? I18n.t('contract.adelantoNex.status.headerPending')
        : I18n.t('contract.adelantoNex.status.headerSent', {
              count: formatAdelantoNexAmount(Number(nexAmount ?? 0)),
          });

    return (
        <span
            className={[
                'adelanto-status-pill',
                `adelanto-status-pill--${isNotSent ? 'pending' : 'sent'}`,
                `adelanto-status-${token}`,
                className,
            ]
                .filter(Boolean)
                .join(' ')}
            data-status={status}
        >
            {pillLabel}
        </span>
    );
};

AdelantoStatusBadge.propTypes = {
    status: PropTypes.string,
    className: PropTypes.string,
    variant: PropTypes.oneOf(['header', 'inline']),
    nexAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default AdelantoStatusBadge;
